/**
 * Dialog — модальное окно (оплата/шаблон/подтверждение). Оверлей + центрированная панель.
 * Доступность: role="dialog" aria-modal, фокус-трап, Esc и клик по оверлею закрывают,
 * возврат фокуса на инициатор. Оверлей рендерится внутри дерева виджета (без порталов в document.body —
 * iframe изолирован, а глобального fixed-контекста избегаем прокруткой внутри .pw-app).
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { cx } from './cx';
import { useDismiss } from './useDismiss';
import { useFocusTrap } from './useFocusTrap';
import { Button } from './Button';
import { IconX } from './icons';

export interface DialogProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Кнопки футера (справа). Если не заданы — футер не рендерится. */
  footer?: ReactNode;
  /** Ширина панели. */
  size?: 'sm' | 'md';
  className?: string;
}

export function Dialog({ title, onClose, children, footer, size = 'sm', className }: DialogProps): JSX.Element {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useRef(`pw-dlg-${Math.random().toString(36).slice(2, 9)}`).current;

  useDismiss({ enabled: true, onDismiss: onClose, refs: [panelRef] });
  useFocusTrap(panelRef, true);

  // Виджет живёт в авто-растущем iframe: «видимая область» — у родительской страницы amoCRM.
  // scrollIntoView сквозь границу фрейма докручивает страницу amo так, чтобы диалог
  // (и кнопки футера) оказались на экране пользователя.
  useEffect(() => {
    const t = window.setTimeout(() => {
      panelRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 50);
    return () => window.clearTimeout(t);
  }, []);

  return (
    <div className="pw-overlay">
      <div
        ref={panelRef}
        className={cx('pw-dialog', size === 'md' && 'pw-dialog--md', className)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="pw-dialog__head">
          <h2 id={titleId} className="pw-dialog__title">
            {title}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            className="pw-icon-btn"
            aria-label="Закрыть"
            onClick={onClose}
            icon={<IconX size={16} />}
          />
        </div>
        <div className="pw-dialog__body">{children}</div>
        {footer ? <div className="pw-dialog__footer">{footer}</div> : null}
      </div>
    </div>
  );
}
